import axiosUserInstance from "../axiosUserInstance";

export const uploadFileToS3 = async (file, saveToDb = false) => {
  if (!file) throw new Error("No file provided");
  const formData = new FormData();
  formData.append("image", file);
  formData.append("saveToDb", saveToDb);
  try {
    const response = await axiosUserInstance.post("/file/upload", formData);
    if (response?.status === 200) {
      return response.data.fileURL;
    } else {
      throw new Error("File upload failed");
    }
  } catch (err) {
    console.error("File upload error:", err);
    throw err;
  }
};

export const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const extractYoutubeId = (url) => {
  // Handles these formats:
  // https://youtu.be/xxxxx
  // https://www.youtube.com/watch?v=xxxxx
  // https://www.youtube.com/embed/xxxxx

  let id = "";
  if (url.includes("youtu.be")) {
    id = url.split("youtu.be/")[1];
  } else if (url.includes("watch?v=")) {
    id = url.split("watch?v=")[1].split("&")[0];
  } else if (url.includes("/embed/")) {
    id = url.split("/embed/")[1];
  }
  return id;
};

export const handleDownload = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "file");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const toDateTimeLocal = (value) => {
  if (!value) return "";

  let date;

  // ISO or timestamp
  if (!isNaN(Date.parse(value))) {
    date = new Date(value);
  } 
  // DD/MM/YYYY, HH:mm:ss
  else {
    const [datePart, timePart] = value.split(", ");
    if (!datePart || !timePart) return "";

    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    date = new Date(year, month - 1, day, hours, minutes);
  }

  if (isNaN(date.getTime())) return "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");

  return `${y}-${m}-${d}T${h}:${min}`;
};

export const parseRatio = (ratioStr) => {
  const [w, h] = ratioStr.split(":").map(Number);
  return w / h;
};

export const deleteImage = async (imageId) => {
  try {
    const response = await axiosUserInstance.delete(`/file/image/${imageId}`);
    if (response?.status === 200) {
      return { success: true, message: response.data.message };
    } else {
      throw new Error("Delete image failed");
    }
  } catch (err) {
    console.error("Delete image error:", err);
    throw err;
  }
};

export const getUserImages = async (page = 1, limit = 20) => {
  try {
    const response = await axiosUserInstance.get("/file/user-images", {
      params: { page, limit },
    });
    if (response?.status === 200) {
      return response.data;
    } else {
      throw new Error("Failed to fetch user images");
    }
  } catch (err) {
    console.error("Get user images error:", err);
    throw err;
  }
};

export const downloadFile = async (url, fileName) => {
  try {
    // Use backend proxy so auth/cookies are forwarded and CORS is avoided
    const response = await axiosUserInstance.get('/file/download-proxy', { params: { url }, responseType: 'blob' });
    const blob = response.data;
    
    // Extract filename from Content-Disposition header or use provided fileName
    let suggestedName = fileName;
    if (!suggestedName && response.headers['content-disposition']) {
      const matches = response.headers['content-disposition'].match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (matches && matches[1]) {
        suggestedName = matches[1].replace(/['"]/g, '');
      }
    }
    suggestedName = suggestedName || 'download';

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = suggestedName; // Use 'download' property instead of setAttribute
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    
  } catch (err) {
    console.error('Download error:', err);
    
    // Fallback: try fetching with native fetch API
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (fallbackErr) {
      console.error('Fallback download failed:', fallbackErr);
      throw new Error('Unable to download file. Please try again.');
    }
  }
};

export const getFileType = (mimeType) => {
  if (!mimeType) return "file";
  
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("application/pdf")) return "pdf";
  if (mimeType.includes("document") || mimeType.includes("word") || mimeType.includes("text")) return "document";
  
  return "file";
};

export const downloadMultipleFiles = async (files, zipFileName = "media.zip") => {
  // This would require a backend endpoint to create a zip file
  // For now, download files individually, sequentially, awaiting each
  for (const file of files) {
    try {
      await downloadFile(file.imageUrl, file.fileName);
    } catch (err) {
      console.error(`Failed to download ${file.fileName}:`, err);
      // continue with next file
    }
    // small delay to avoid triggering browser blocking
    await new Promise(resolve => setTimeout(resolve, 300));
  }
};
